import React, { useState, useEffect, useMemo } from 'react';
import { FileText, BarChart2, Calendar } from 'lucide-react';
import { __ } from './translations';
import EditButton from './components/EditButton';
import YearTabs from './components/YearTabs';
import SearchInput from './components/SearchInput';
import TagList from './components/TagList';
import { 
  createFuseInstance, 
  preprocessItems, 
  performSearch,
  createYearFilter,
  createTagFilter
} from './utils/fuseSearch';

const SurveysAnalysis = () => {
    const [items, setItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeYear, setActiveYear] = useState('all');
    const [availableYears, setAvailableYears] = useState([]);
    const [activeTag, setActiveTag] = useState('');

    useEffect(() => {
        const fetchSurveysAnalysis = async () => {
            try {
                const response = await fetch(
                    `${boardMemberData.restURL}board-member/v1/member-information/surveys-analysis`,
                    {
                        headers: {
                            'X-WP-Nonce': boardMemberData.nonce
                        }
                    }
                );
                
                if (!response.ok) throw new Error('Network response was not ok');
                const data = await response.json();
                
                // Extract unique publication years
                const years = [...new Set(
                    data
                        .filter(item => item.publication_year)
                        .map(item => item.publication_year)
                )];
                
                setAvailableYears(years);
                setItems(data);
                setFilteredItems(data);
            } catch (err) {
                setError('Failed to load surveys and analysis');
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchSurveysAnalysis();
    }, []);

    // Preprocess items for search
    const processedItems = useMemo(() => {
        return preprocessItems(items);
    }, [items]);

    // Create Fuse instance
    const fuse = useMemo(() => {
        if (!processedItems.length) return null;
        
        const options = {
            keys: [
                { name: 'title', weight: 0.7 },
                { name: 'content', weight: 0.5 },
                { name: 'excerpt', weight: 0.3 },
                { name: 'tagString', weight: 0.9 }
            ]
        };
        
        return createFuseInstance(processedItems, options);
    }, [processedItems]);

    // Filter items based on search term, year, and tag
    useEffect(() => {
        if (!fuse) {
            setFilteredItems(items);
            return;
        }
        
        // Create filter functions
        const filters = {
            yearFilter: createYearFilter(activeYear),
            tagFilter: createTagFilter(activeTag)
        };
        
        // Perform search with filters
        const results = performSearch(fuse, searchTerm, filters);
        setFilteredItems(results);
    }, [fuse, searchTerm, activeYear, activeTag, items]);

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    const handleTagClick = (tagName) => {
        setActiveTag(tagName === activeTag ? '' : tagName);
    };

    // Reset all filters
    const resetFilters = () => {
        setActiveYear('all');
        setActiveTag('');
        setSearchTerm('');
    };

    if (loading) {
        return <div className="text-center py-6">{__('Loading...')}</div>;
    }

    if (error) {
        return <div className="text-red-500 py-6">{error}</div>;
    }

    if (!items.length) {
        return <div className="text-center py-6">{__('No surveys or analysis available.')}</div>;
    }

    // Check if filters are active
    const hasActiveFilters = activeYear !== 'all' || activeTag !== '' || searchTerm !== '';

    return (
        <>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">{__('Surveys and Analysis')}</h2>
                <div className="w-1/4">
                    <SearchInput
                        onSearch={handleSearch}
                        placeholder={__('Search surveys and analysis...')}
                        value={searchTerm}
                    />
                </div>
            </div>
            
            {availableYears.length > 0 && (
                <YearTabs
                    years={availableYears}
                    activeYear={activeYear}
                    setActiveYear={setActiveYear}
                    isSurveysAnalysis
                />
            )}
            
            {/* Active tag display */}
            {activeTag && (
                <div className="mb-4 flex items-center">
                    <span className="mr-2">{__('Active tag:')}</span>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {activeTag}
                    </span>
                    <button 
                        onClick={() => setActiveTag('')}
                        className="ml-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                        {__('Clear')}
                    </button>
                </div>
            )}
            
            {/* Reset filters button - show only when filters are active */}
            {hasActiveFilters && (
                <div className="mb-4">
                    <button 
                        onClick={resetFilters}
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                        {__('Reset all filters')}
                    </button>
                </div>
            )}
            
            {filteredItems.length === 0 ? (
                <div className="text-center py-6">{__('No items match your filters.')}</div>
            ) : (
                <div className="space-y-6">
                    {filteredItems.map((item) => (
                        <div 
                            key={item.id} 
                            className="bg-white rounded-lg shadow-md border border-gray-200"
                        >
                            <div className="p-6">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <BarChart2 className="w-5 h-5 text-blue-600" />
                                        <h3 className="text-xl font-semibold">{item.title}</h3>
                                    </div>
                                    <EditButton postId={item.id} />
                                </div>
                                
                                <div className="flex items-center text-sm text-gray-500 mb-2">
                                    <Calendar className="w-4 h-4 mr-2" />
                                    <time>{new Date(item.date).toLocaleDateString()}</time>
                                    {item.publication_year && (
                                        <span className="ml-2 px-2 py-1 text-xs bg-gray-100 rounded-full">
                                            {item.publication_year}
                                        </span>
                                    )}
                                </div>
                                
                                {/* Tags */}
                                {item.tags && item.tags.length > 0 && (
                                    <TagList 
                                        tags={item.tags} 
                                        onTagClick={handleTagClick}
                                        className="mb-3" 
                                    />
                                )}
                                
                                <div 
                                    className="prose max-w-none mb-6"
                                    dangerouslySetInnerHTML={{ __html: item.content }}
                                />

                                {item.pdf_url && (
                                    <div className="pt-4 border-t mt-4">
                                        <a
                                            href={item.pdf_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                        >
                                            <FileText className="w-4 h-4 mr-2" />
                                            {__('Download Full Report')}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export default SurveysAnalysis;